import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, setStoredToken } from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { consumePostAuthRedirect } from "@/utils/postAuthRedirect";

/**
 * Handles the return from Emergent Google OAuth.
 * URL looks like: <origin>/dashboard#session_id=xxxxx
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS — THIS BREAKS THE AUTH.
 * The redirect_url passed to auth.emergentagent.com is derived from window.location.origin on the login page.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  // Use ref (not state) so StrictMode's double-invoke doesn't fire twice
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const { data } = await api.post("/api/auth/google/session", { session_id: sessionId });
        if (data.session_token) setStoredToken(data.session_token);
        // Clear the hash so the session_id never re-triggers
        window.history.replaceState(null, "", window.location.pathname);
        await refreshUser();
        if (data.needs_profile_completion) {
          navigate("/complete-profile", { replace: true });
        } else {
          navigate(consumePostAuthRedirect(), { replace: true });
        }
      } catch (e) {
        const msg = formatApiError(e);
        navigate(`/login?error=${encodeURIComponent(msg)}`, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F1C3F] text-slate-200">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-400">Signing you in with Google…</p>
      </div>
    </div>
  );
}
